import { each, sortBy } from 'lodash'
import { asyncForEach } from 'common/utils/asyncForEach'
import { createUID } from 'common/utils/createUID'
import { getNewPart, getNewExercise } from '../programModels'
import { getNumSets } from './instructionsSetsUtils'
import { EMPTY_TIME_STRING } from './timeUtils'
import { getFirebase } from 'modules/App/globalApi'

export const prepDraftProgram = (program) => {
  if (!program || !program.workouts) {
    return {
      wktsByWeek: {},
      numWeeks: 1,
      hasWorkouts: false,
      wktIdsSorted: [],
      wktIdsToDayIdcs: {},
    }
  }

  let maxDayIdx = 0
  let wktsByWeek = {}
  let hasWorkouts = false
  let dayIdxToIds = {}
  let wktIdToMultiWkts = {}
  let multiWktDaysByIdx = {}

  each(program?.multiWktDays || {}, (day) => {
    const multiWktDayIdx = day?.dayIdx
    multiWktDaysByIdx[multiWktDayIdx] = day
  })

  each(program.workouts, (dayIdx, wktId) => {
    if (dayIdx || dayIdx === 0) hasWorkouts = true

    maxDayIdx = Math.max(dayIdx, maxDayIdx)
    const multiWktsInfo = multiWktDaysByIdx?.[dayIdx]
    if (multiWktsInfo) {
      wktIdToMultiWkts[wktId] = multiWktsInfo?.workouts
    }
    orgWorkoutsToWeeks(dayIdx, wktId, wktsByWeek, multiWktsInfo)
    dayIdxToIds[dayIdx] = wktId
  })

  const dayIdxToIdsSortedEntries = sortBy(Object.entries(dayIdxToIds), ([dayIdx]) => Number(dayIdx))
  const wktIdsSorted = dayIdxToIdsSortedEntries.map(([, wktId]) => wktId)
  const wktIdsToDayIdcs = { ...program.workouts }
  program.workouts = null

  return {
    wktsByWeek,
    numWeeks: Math.floor(maxDayIdx / 7) + 1,
    hasWorkouts,
    wktIdsSorted,
    wktIdsToDayIdcs,
    wktIdToMultiWkts,
  }
}

export const prepWorkout = (wkt) => {
  const workout = { ...wkt }

  if (!workout.id) {
    workout.id = createUID()
  }

  if (!workout.parts) {
    workout.parts = []
  }

  if (!workout.type) {
    if (wkt.activity) {
      workout.type = wkt.activity
    }

    if (wkt.title && wkt.title.toLowerCase().includes('rest day')) {
      workout.type = 'rest'
    }
  }

  each(workout.parts, (part, partIdx) => {
    if (workout.type === 'rest') {
      return
    }

    if (!part) {
      const newPart = getNewPart()
      workout.parts[partIdx] = newPart
      part = newPart
    }

    if (!part.id) {
      part.id = createUID()
    }

    if (!part.exercises) {
      const emptyExercise = getNewExercise()
      workout.parts[partIdx].exercises = [emptyExercise]
    }

    if (!part.sets) {
      part.sets = getNumSets(part.instructions)
    }

    each(part.exercises, (ex) => {
      if (!ex || ex.dndUID) return

      ex.dndUID = createUID()
    })
  })

  return workout
}

const orgWorkoutsToWeeks = (dayIdx, wktId, wktsByWeek, multiWktsInfo) => {
  const weekIdx = Math.floor(dayIdx / 7)
  const idxDayOfWk = dayIdx % 7

  if (!wktsByWeek[weekIdx]) {
    wktsByWeek[weekIdx] = []
  }

  const wktData = {
    id: wktId,
    dayIdx: dayIdx,
    multiWktsInfo,
  }

  wktsByWeek[weekIdx][idxDayOfWk] = wktData
}

export const cleanDraftProgramForPublishing = (workouts) => {
  const cleaned = { ...workouts }

  each(workouts, (wkt, wktId) => {
    const shouldRemoveWkt = getShouldRemoveWkt(wkt, wktId)

    if (shouldRemoveWkt) {
      cleaned[wktId] = null
    } else {
      let cleanedWorkout = cleanWorkout(wkt, true)
      delete cleanedWorkout.verifiedDurationEst
      cleaned[wktId] = cleanedWorkout
    }
  })

  return cleaned
}

export const cleanMultiWktsForPublishing = ({ multiWkts, multiWktDays }) => {
  let cleanedWorkouts = { ...multiWkts }
  let updatedMultiWktDays = { ...multiWktDays }

  each(multiWktDays, (wktDayInfo, wktDayId) => {
    let multiWktIds = wktDayInfo?.workouts || []
    let multiWktIdsAdjusted = [...multiWktIds]

    multiWktIds.forEach((wktId) => {
      const wkt = multiWkts?.[wktId]
      const cleanedWorkout = cleanWorkout(wkt, true)
      const shouldRemoveWkt = getShouldRemoveWkt(cleanedWorkout, wktId)
      if (shouldRemoveWkt || (wkt?.type === 'single_video' && !wkt?.videoId)) {
        cleanedWorkouts[wktId] = null
        multiWktIdsAdjusted = multiWktIdsAdjusted.filter((id) => id !== wktId)
      } else {
        cleanedWorkouts[wktId] = cleanedWorkout
      }
    })

    // still multiWkt after cleanup
    if (multiWktIdsAdjusted.length > 1) {
      multiWktIdsAdjusted.forEach((wktId, index) => {
        const wkt = cleanedWorkouts?.[wktId]
        cleanedWorkouts[wktId] = {
          ...wkt,
          dayIdx: index === 0 ? wktDayInfo?.dayIdx : null, // only first workout should have dayIdx for the app logic
          multiWktIdx: index,
        }
      })
      updatedMultiWktDays[wktDayId] = { ...updatedMultiWktDays[wktDayId], workouts: multiWktIdsAdjusted }
    } else {
      // no longer multiWkt (1 wkt might still remain, making sure I handle it by making it regular wkt)
      multiWktIdsAdjusted.forEach((wktId) => {
        const wkt = cleanedWorkouts?.[wktId]
        cleanedWorkouts[wktId] = { ...wkt, dayIdx: wktDayInfo?.dayIdx, multiWktIdx: null, multiWktDayId: null }
      })
      updatedMultiWktDays[wktDayId] = null
    }
  })

  return { cleanedWorkouts, updatedMultiWktDays }
}

const getShouldRemoveWkt = (workout, workoutId) => {
  if (!workout) {
    return true
  }

  if (!workoutId || workoutId === 'null' || workoutId === 'undefined') {
    return true
  }

  //remove empty workouts
  if (workout.type === 'empty') {
    return true
  }

  if (workout.type === 'rest') {
    return false
  }

  if (workout.type === 'single_video') {
    return false
  }

  if (!workout.parts || !workout.parts.length) {
    return true
  }

  return false
}

export const cleanWorkout = (workout, isForPublishing) => {
  const newWkt = JSON.parse(JSON.stringify(workout))
  const parts = newWkt.parts || []

  const cleanedParts = parts
    .map((pt) => {
      if (pt?.type === 'rest') {
        return pt
      }

      let part = { ...pt }
      let cleanedExercises = part.exercises.filter((ex) => Boolean(ex.name.trim()))

      cleanedExercises.forEach((ex) => {
        if (ex.instructions && typeof ex.instructions === 'string') {
          ex.instructions = ex.instructions.trim()
        }

        if (isForPublishing) {
          ex.dndUID = null

          if (ex.time === EMPTY_TIME_STRING) {
            ex.time = null
          }

          if (ex.rest === EMPTY_TIME_STRING) {
            ex.rest = null
          }
        }
      })

      part.exercises = cleanedExercises
      return part
    })
    .filter((pt) => Boolean(pt.exercises.length))

  newWkt.parts = cleanedParts

  if (!newWkt.parts.length && !newWkt.title && !newWkt.instructions && !newWkt.multiWktDayId) {
    newWkt.type = 'empty'
    newWkt.activity = null
  }

  return newWkt
}

export const findKeyByValue = (value, obj) => {
  let neededKey
  for (let key in obj) {
    if (obj[key] === value) {
      neededKey = key
    }
  }
  return neededKey
}

const fetchDraftProgramWorkouts = async ({ orgId, programId }) => {
  try {
    const path = `drafts/${orgId}/programs/${programId}/workouts`
    const { data } = await getFirebase(path)
    return data
  } catch (e) {
    console.error('fetchDraftProgram e', e)
  }
}

const fetchDraftProgramMultiWktDays = async ({ orgId, programId }) => {
  try {
    const path = `drafts/${orgId}/programs/${programId}/multiWktDays`
    const { data } = await getFirebase(path)
    return data
  } catch (e) {
    console.error('fetchDraftProgram e', e)
  }
}

const fetchDraftWorkout = async ({ orgId, workoutId }) => {
  try {
    const path = `drafts/${orgId}/workouts/${workoutId}`
    const { data } = await getFirebase(path)
    return data
  } catch (e) {
    console.error('fetchDraftWorkout e', e)
  }
}

/**
 * Retrieves workouts for a program from state and fetches missing ones if needed.
 *
 * @async
 * @param {Object} params
 * @param {any} params.state - Application state containing program workouts.
 * @param {string} params.orgId - Org ID.
 * @param {string} params.programId - Program ID.
 * @param {Array} params.wktIds - Array of workout IDs to retrieve.
 *
 * @returns {Promise<{
 *   workouts: Object<string, any> | null,
 *   error: Error | undefined
 * }>} An object containing the workouts map and an optional error.
 */
export const getProgramWorkouts = async ({ state, orgId, programId, wktIds }) => {
  let workouts, error

  try {
    const allStateWkts = state.program.workouts

    const programStateWkts = {}
    let numWktsFetched = 0
    wktIds.forEach((wktId) => {
      const workout = allStateWkts[wktId]
      programStateWkts[wktId] = workout

      if (workout) {
        numWktsFetched++
      }
    })

    const numWktsProg = wktIds.length

    let fetchedWktsPrepped = null
    if (numWktsFetched !== numWktsProg) {
      const draftProgramWkts = await fetchDraftProgramWorkouts({ orgId, programId })
      const multiWktDays = await fetchDraftProgramMultiWktDays({ orgId, programId })

      let programMultiWkts = {}
      Object.values(multiWktDays || {}).forEach((day) => {
        const multiWktIds = day?.workouts || []
        multiWktIds.forEach((wktId) => (programMultiWkts[wktId] = true))
      })

      const wktsPrepped = {}
      const fullWktsToFetch = { ...draftProgramWkts, ...programMultiWkts }
      await asyncForEach(fullWktsToFetch, async (_, workoutId) => {
        const workout = await fetchDraftWorkout({ orgId, workoutId })
        const prepped = prepWorkout(workout)
        wktsPrepped[workoutId] = prepped
      })
      fetchedWktsPrepped = wktsPrepped
    }

    workouts = fetchedWktsPrepped || programStateWkts
  } catch (e) {
    error = e
    console.error(e)
  } finally {
    return { workouts, error }
  }
}

export const getWorkoutsByIds = async ({ state, orgId, wktIds, prioritizeCopied = false }) => {
  let workouts = {}

  each(wktIds, async (wktId) => {
    const wktFromState = prioritizeCopied
      ? state.program.multiWktsCopied[wktId] || state.program.workouts[wktId]
      : state.program.workouts[wktId]

    if (wktFromState) {
      workouts[wktId] = wktFromState
    } else {
      try {
        const workout = await fetchDraftWorkout({ orgId, workoutId: wktId })
        const prepped = prepWorkout(workout)
        workouts[wktId] = prepped
      } catch (e) {
        console.error(e)
      }
    }
  })

  return workouts
}

export const getDuplicatedProgramWkts = ({ workouts }) => {
  const duplicatedWkts = {}
  const updatedWktIdsToDayIdcs = {}

  each(workouts, (wkt) => {
    const updatedWkt = { ...wkt, parts: [], id: createUID() }

    each(wkt.parts, (part) => {
      const updatedPart = { ...part, exercises: [], id: createUID() }

      each(part.exercises, (ex) => {
        updatedPart.exercises.push({ ...ex, dndUID: createUID() })
      })

      updatedWkt.parts.push(updatedPart)
    })

    duplicatedWkts[updatedWkt.id] = updatedWkt
    updatedWktIdsToDayIdcs[updatedWkt.id] = updatedWkt.dayIdx
  })

  return { duplicatedWkts, updatedWktIdsToDayIdcs }
}

export const getDuplicatedProgramMultiWkts = ({ multiWkts, multiWktDays }) => {
  const duplicatedMultiWkts = {}
  const updatedMultiWktIdsToDayIdcs = {}
  const updatedMultiWktDays = {}

  each(multiWktDays, (wktDayInfo) => {
    const multiWktIds = wktDayInfo?.workouts || []
    let multiWktIdsAdjusted = []
    const newMultiWktDayId = createUID()

    multiWktIds.forEach((wktId, index) => {
      const wkt = multiWkts?.[wktId]
      const updatedWkt = { ...wkt, parts: [], id: createUID(), multiWktDayId: newMultiWktDayId }

      each(wkt.parts, (part) => {
        const updatedPart = { ...part, exercises: [], id: createUID() }

        each(part.exercises, (ex) => {
          updatedPart.exercises.push({ ...ex, dndUID: createUID() })
        })

        updatedWkt.parts.push(updatedPart)
      })

      multiWktIdsAdjusted.push(updatedWkt.id)
      duplicatedMultiWkts[updatedWkt.id] = updatedWkt
      // main wkt of multiWkt is index = 0, so use it for updatedMultiWktIdsToDayIdcs
      if (index === 0) {
        updatedMultiWktIdsToDayIdcs[updatedWkt.id] = updatedWkt.dayIdx
      }
    })

    updatedMultiWktDays[newMultiWktDayId] = {
      ...wktDayInfo,
      workouts: multiWktIdsAdjusted,
      id: newMultiWktDayId,
    }
  })

  return { duplicatedMultiWkts, updatedMultiWktIdsToDayIdcs, updatedMultiWktDays }
}

export const getDuplicatedProgram = ({ newProgramId, program, updatedWktIdsToDayIdcs, updatedMultiWktDays }) => {
  const updatedProgram = {
    ...program,
    id: newProgramId,
    name: `${program.name} Copy`,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    publishedAt: null,
    workouts: updatedWktIdsToDayIdcs,
    multiWktDays: updatedMultiWktDays,
  }

  return updatedProgram
}
